import dayjs from 'dayjs';
// Helpers
import {
  getDateInOriginalTimeZone,
  readWallClockInZone,
  toLegacyScheduleDateString,
} from 'helpers/timeZone';
import { TimeZoneOption } from 'constants/timeZone';
import {
  computeOrderStreamPath,
  computeVideoWatchStreamPath,
} from 'helpers/routes';
import { getIsSponsoredAppearance } from 'helpers/campaign';
// Types
import {
  GetStream_stream,
  GetStream_stream_store,
  GetStream_stream_store_storeDetails,
} from 'api/streams/types/GetStream';
import {
  GetAllAmas_amas_entities_store,
  GetAllAmas_amas_entities_store_storeDetails,
} from 'api/ama/types/GetAllAmas';
import {
  GetAllHomePageData_getHomePageData_merch_store,
  GetAllHomePageData_getHomePageData_watchStreams_store,
  GetAllHomePageData_getHomePageData_amas_store_storeDetails,
  GetAllHomePageData_getHomePageData_athleteStories_store,
  GetAllHomePageData_featuredPpvEvents_stream_store,
} from 'api/homePage/types/GetAllHomePageData';
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { StreamComputeParams } from 'helpers/routes';
import {
  CreateMatchupInput,
  InterviewStatus,
  StreamCreateInput,
  StreamIntervalEnum,
  StreamStatus,
  TimeZoneInput,
} from 'api/graphql-global-types';
import { MyStreams_myStreams_entities } from 'api/streams/types/MyStreams';
import { GetUpcomingStreams_streams_entities_store } from 'api/streams/types/GetUpcomingStreams';
import { GetMerchProducts_getMerchProducts_entities_store } from 'api/merch/types/GetMerchProducts';
import { MyOutcomingStreamOrders_myOutcomingStreamOrders_entities_stream } from 'api/streams/types/MyOutcomingStreamOrders';
import { HomepageSearch_streamsV2_entities_store } from 'api/search/types/HomepageSearch';
import { GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignLiveAppearanceSubmission_stream } from 'api/campaignV2/types/GetMyCampaignOfferByCampaignId';
import { VideoDetails } from 'components/common3/VideoDetails/VideoDetails';
// Constants
import { TIME_ZONE_OPTIONS } from 'constants/timeZone';
import {
  MANAGE_AMA,
  SCHEDULE_STREAM,
  USER_SCHEDULE_INTERVIEW,
} from 'constants/routes';
import { STREAM_TAG_MAX_LENGTH } from 'constants/streams';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ContentStreamStatus } from 'ui3/Card/ContentCard';
import { formatVideoDetailsDate } from './dateText';
import { GetFeaturedStreams_featuredPpvEvents_stream_store } from 'api/homePage/types/GetFeaturedStreams';
import { GetStoreMerchProduct_getStoreMerchProduct_store } from 'api/merch/types/GetStoreMerchProduct';
import { GetMemorabiliaBySlug_getMemorabiliaBySlug_store } from 'api/memorabilia/types/GetMemorabiliaBySlug';
import { GetExperienceBySlug_getExperienceBySlug_store } from 'api/experiences/types/GetExperienceBySlug';
import { PublicTicketEventFields_host } from 'api/ticketEvents/types/PublicTicketEventFields';

dayjs.extend(utc);
dayjs.extend(timezone);

type Stream =
  | GetStream_stream
  | MyStreams_myStreams_entities
  | MyOutcomingStreamOrders_myOutcomingStreamOrders_entities_stream
  | undefined;

export const isStreamDisabled = (
  stream: Pick<NonNullable<Stream>, 'streamStatus'> | null | undefined
): boolean => {
  return (
    stream?.streamStatus === StreamStatus.Cancelled ||
    stream?.streamStatus === StreamStatus.Ended
  );
};

export const isStreamUnableToEdit = (stream: Stream): boolean => {
  // if stream is not an interview, not ended or scheduled or if it is an interview and has ended
  return (
    (stream?.streamStatus !== StreamStatus.Scheduled &&
      stream?.streamStatus !== StreamStatus.Ended &&
      !stream?.isInterview) ||
    (stream?.streamStatus === StreamStatus.Ended && stream.isInterview)
  );
};

export const getEditStreamPath = (
  stream: MyStreams_myStreams_entities
): string => {
  if (stream.isInterview) {
    return `${USER_SCHEDULE_INTERVIEW}/interview/${stream.interview?.id}`;
  }

  if (getIsSponsoredAppearance(stream)) {
    return `${MANAGE_AMA}/edit-scheduled-appearance/${stream.id}`;
  }

  return `${SCHEDULE_STREAM}/${stream.id}`;
};

export const isStartStreamButtonDisabled = (stream: Stream): boolean => {
  const isStreamEndedOrCanceled =
    stream?.streamStatus === StreamStatus.Ended ||
    stream?.streamStatus === StreamStatus.Cancelled;

  if (isStreamEndedOrCanceled) {
    return true;
  }

  if (stream?.scheduleDate) {
    const currentTime = dayjs();
    const streamScheduleTime = dayjs(stream.scheduleDate).subtract(
      stream?.timeZone?.offset || 0,
      'minute'
    );
    const anHourBeforeStream = streamScheduleTime.subtract(1, 'hour');
    const isStreamShouldBeStartedSoon = currentTime.isAfter(anHourBeforeStream);

    return !isStreamShouldBeStartedSoon;
  }

  return false;
};

export const isStartStreamButtonDisabledV2 = (
  stream: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignLiveAppearanceSubmission_stream
): boolean => {
  if (!stream) return false;

  const isStreamEndedOrCanceled =
    stream?.streamStatus === StreamStatus.Ended ||
    stream?.streamStatus === StreamStatus.Cancelled;

  if (isStreamEndedOrCanceled) {
    return true;
  }

  if (stream?.scheduleDateUtc && stream?.scheduleDateTz) {
    const currentTime = dayjs().tz(stream.scheduleDateTz);
    const streamScheduleTime = dayjs(stream.scheduleDateUtc).tz(
      stream.scheduleDateTz
    );
    const anHourBeforeStream = streamScheduleTime.subtract(1, 'hour');
    const isStreamShouldBeStartedSoon = currentTime.isAfter(anHourBeforeStream);

    return !isStreamShouldBeStartedSoon;
  }

  return false;
};

export const getStreamDate = (
  date: string | null,
  timeZone?: TimeZoneInput | null | undefined
): string => {
  if (date && dayjs(date).isValid()) {
    const dateString = getDateInOriginalTimeZone(date, 'MM/DD/YYYY [at] h:mmA');
    return `${dateString} ${timeZone?.tzCode || ''}`;
  }

  return '\u2014';
};

const getTimeZoneName = (tzCode: string) => {
  const timeZoneObj = TIME_ZONE_OPTIONS.find(
    (option) => option.tzCode === tzCode
  );
  return timeZoneObj ? timeZoneObj.tzName : null;
};

const isIanaTimeZone = (tzCode: string) => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tzCode });
    return true;
  } catch {
    return false;
  }
};

/** An unknown code must not take a whole stream list down, so it reads as an IANA zone or ET. */
const convertToTimeZone = (date: string, tzCode: string) => {
  const timeZone =
    getTimeZoneName(tzCode) ||
    (isIanaTimeZone(tzCode) ? tzCode : TIME_ZONE_OPTIONS[0].tzName);

  return dayjs.tz(date, timeZone);
};

export const getStreamDateV2 = (scheduleDateUtc: string | null): string => {
  if (scheduleDateUtc && dayjs(scheduleDateUtc).isValid()) {
    const dateInLocal = dayjs.utc(scheduleDateUtc).local();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeFormatted = dateInLocal.format('MM/DD/YYYY h:mm A');
    return `${timeFormatted.toString()} ${timeZone}`;
  }

  return '\u2014';
};

export const getStreamDateShortFormat = (
  scheduleDateUtc: string | null
): string => {
  if (scheduleDateUtc && dayjs(scheduleDateUtc).isValid()) {
    const dateInLocal = dayjs.utc(scheduleDateUtc).local();
    const dayOfWeek = dateInLocal.format('ddd').toUpperCase();
    const day = dateInLocal.date();
    const month = dateInLocal.format('MMM').toUpperCase();
    const timeFormatted = dateInLocal.format('hh:mmA');
    return `${dayOfWeek} ${day} ${month} ${timeFormatted}`;
  }

  return '\u2014';
};

export const getStreamWatchAvailableDate = (
  scheduleDateUtc: string | null
): string => {
  if (scheduleDateUtc && dayjs(scheduleDateUtc).isValid()) {
    return dayjs.utc(scheduleDateUtc).local().format('MMM D YYYY');
  }

  return '';
};

export const getStreamCardDate = (
  date: string | null,
  tzCode: string | null,
  place: 'description' | 'label'
): string => {
  if (date && dayjs(date).isValid()) {
    const dateInTzCode = convertToTimeZone(date, tzCode || 'ET');
    const dateInLocal = dateInTzCode.local();
    const day = dateInLocal.date();
    const month = dateInLocal.format('MMM');
    const year = dateInLocal.format('YYYY');
    const timeFormatted = dateInLocal.format('hh:mm A');

    if (place === 'label') {
      return `${day} ${month} AT ${dateInLocal.format('hA')}`;
    } else {
      return `${day} ${month} ${year} at ${timeFormatted}`;
    }
  }

  return '\u2014';
};

export const getStreamStatus = (
  repeatsEveryEnum: StreamIntervalEnum | null,
  isHidden: boolean | null,
  streamStatus: ContentStreamStatus | StreamStatus
): ContentStreamStatus | StreamStatus | StreamIntervalEnum => {
  if (isHidden) {
    return ContentStreamStatus.HIDDEN;
  }
  if (repeatsEveryEnum) {
    // If repeated stream is active, return active
    return streamStatus === StreamStatus.Active
      ? streamStatus
      : repeatsEveryEnum;
  }
  return streamStatus;
};

export const getPublicStreamDate = (
  date: string | null | undefined,
  timeZone?: TimeZoneInput | null | undefined
): any => {
  if (date && dayjs(date).isValid()) {
    const dateString = getDateInOriginalTimeZone(
      date,
      'MMMM D, YYYY [at] h:mmA'
    );
    return `${dateString}${timeZone?.tzCode ? ` ${timeZone?.tzCode}` : ''}`;
  }

  return '\u2014';
};

export const getStreamerName = (
  store:
    | GetStream_stream_store
    | GetAllAmas_amas_entities_store
    | GetUpcomingStreams_streams_entities_store
    | GetAllHomePageData_getHomePageData_merch_store
    | GetMerchProducts_getMerchProducts_entities_store
    | GetAllHomePageData_getHomePageData_watchStreams_store
    | GetStoreBySlug_store
    | GetAllHomePageData_getHomePageData_athleteStories_store
    | HomepageSearch_streamsV2_entities_store
    | GetFeaturedStreams_featuredPpvEvents_stream_store
    | GetAllHomePageData_featuredPpvEvents_stream_store
    | GetStoreMerchProduct_getStoreMerchProduct_store
    | GetMemorabiliaBySlug_getMemorabiliaBySlug_store
    | GetExperienceBySlug_getExperienceBySlug_store
    | PublicTicketEventFields_host
    | null
    | undefined
): string => {
  if (!store) {
    return '';
  }

  const storeName =
    'storeDetails' in store && store.storeDetails?.storeName
      ? store.storeDetails.storeName
      : null;

  if (storeName) {
    return storeName;
  }

  const firstName = 'firstName' in store ? store.firstName : '';
  const lastName = 'lastName' in store ? store.lastName : '';

  return `${firstName || ''} ${lastName || ''}`.trim();
};

export const getStreamerAvatar = (
  storeDetails:
    | GetStream_stream_store_storeDetails
    | GetAllAmas_amas_entities_store_storeDetails
    | GetAllHomePageData_getHomePageData_amas_store_storeDetails
    | null
    | undefined
): string => {
  return storeDetails?.avatarURL || '/images/icons/avatar-placeholder.svg';
};

export const getStreamVideoDetails = (
  stream: GetStream_stream
): VideoDetails => {
  return {
    views: stream.views?.totalViewCount || 0,
    date: formatVideoDetailsDate(stream?.scheduleDate || ''),
    description: stream.description || null,
    mentions: stream.mentions || [],
    hashtags: stream.hashtags || [],
  };
};

export const getTimeRemaining = (
  milliseconds: number
): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  remainingLabel: string;
} => {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

  const dayLabel = days ? `${days}d : ` : '';
  const remainingLabel =
    milliseconds > 0
      ? `${dayLabel}${hours}h : ${minutes}m : ${seconds}s`
      : 'Starting soon';

  return {
    days,
    hours,
    minutes,
    seconds,
    remainingLabel,
  };
};

/**
 * Formats a remaining duration as a compact countdown, e.g. "23h 54m 43s"
 * (or "2d 23h 54m 43s" once there is at least a full day left). Hours, minutes
 * and seconds are zero-padded; the day segment is omitted while under 24h.
 */
export const formatCountdown = (
  milliseconds: number,
  separator = ' '
): string => {
  const { days, hours, minutes, seconds } = getTimeRemaining(milliseconds);
  const pad = (value: number): string =>
    `${Math.max(value, 0)}`.padStart(2, '0');

  return [
    days > 0 ? `${days}d` : null,
    `${pad(hours)}h`,
    `${pad(minutes)}m`,
    `${pad(seconds)}s`,
  ]
    .filter(Boolean)
    .join(separator);
};

export const getStreamLink = (stream: StreamComputeParams): string => {
  const streamIsPurchased = Boolean(stream?.isPurchased);
  const orderStreamPath = computeOrderStreamPath(stream);
  const watchStreamPath = computeVideoWatchStreamPath(stream);

  return streamIsPurchased ? watchStreamPath : orderStreamPath;
};

export const getStreamSocialShareText = (
  isUserCreator: boolean,
  isPastStream: boolean,
  streamerName: string
): string => {
  if (isUserCreator) {
    return isPastStream
      ? 'Check out my newest video on @millionsdotco! Follow my profile on MILLIONS to get my latest content! #sports #MILLIONSco'
      : "Get ready to join me for an awesome live stream event on @millionsdotco! Let's connect and have fun together! Don't miss out! #livestream #sports #MILLIONSco";
  } else {
    return isPastStream
      ? `Check out this video from ${streamerName} on @millionsdotco! #sports #MILLIONSco`
      : `I’m joining ${streamerName} for their upcoming live stream event on @millionsdotco! Don't miss out, you can interact with ${streamerName} live! Come join me in attending it #livestream #sports #MILLIONSco`;
  }
};

export type RepeatStreamOption = {
  value: StreamIntervalEnum | null;
  label: string;
};

export const REPEAT_STREAM_OPTIONS: RepeatStreamOption[] = [
  {
    label: "No, it doesn't repeat",
    value: null,
  },
  {
    label: 'Every week',
    value: StreamIntervalEnum.weekly,
  },
  {
    label: 'Every other week',
    value: StreamIntervalEnum.biweekly,
  },
  {
    label: 'Every month',
    value: StreamIntervalEnum.monthly,
  },
];

/** The picked wall-clock date with the zone offset the stream inputs expect. */
export const getStreamScheduleInput = (
  scheduleDate: Date | string,
  timeZone: TimeZoneOption
): { scheduleDate: string; timeZone: { tzCode: string; offset: number } } => ({
  scheduleDate: toLegacyScheduleDateString(scheduleDate),
  timeZone: {
    tzCode: timeZone.tzCode,
    offset: readWallClockInZone(scheduleDate, timeZone.tzName).utcOffset(),
  },
});

type StreamSaleValues = {
  isFree: boolean;
  price: string;
  fanPollingEnabled: boolean;
  sharePickGraphicsEnabled: boolean;
  affiliateEnabled: boolean;
  affiliateCommissionPercent: string;
};

/** How a new stream sells: its price, card and fan engagement, and affiliate program. */
export const getStreamSaleInput = (
  values: StreamSaleValues,
  matchups: CreateMatchupInput[]
): Pick<
  StreamCreateInput,
  | 'isFree'
  | 'isPPV'
  | 'price'
  | 'matchups'
  | 'fanPollingEnabled'
  | 'sharePickGraphicsEnabled'
  | 'postEventContentHubEnabled'
  | 'affiliateEnabled'
  | 'affiliateCommissionPercent'
  | 'sponsors'
> => {
  const hasMatchups = matchups.length > 0;
  const affiliateEnabled = !values.isFree && values.affiliateEnabled;

  return {
    isFree: values.isFree,
    isPPV: !values.isFree,
    ...(!values.isFree && { price: Number(values.price) }),
    ...(hasMatchups && { matchups }),
    fanPollingEnabled: hasMatchups && values.fanPollingEnabled,
    sharePickGraphicsEnabled: hasMatchups && values.sharePickGraphicsEnabled,
    postEventContentHubEnabled: !values.isFree,
    affiliateEnabled,
    affiliateCommissionPercent: affiliateEnabled
      ? Number(values.affiliateCommissionPercent)
      : null,
    sponsors: [],
  };
};

export const formatHashtagInput = (hashtags: string[] | undefined) => {
  if (hashtags?.length) {
    return hashtags.map((hashtag) => ({ name: hashtag }));
  } else {
    return null;
  }
};

export const formatStreamTag = (value: string): string =>
  value.replace(/[^a-z0-9]/gi, '').slice(0, STREAM_TAG_MAX_LENGTH);

export const getFileName = (storeName: string): string => {
  return storeName
    ? `${storeName} - Stream poster image`
    : 'Stream poster image';
};

export const reformatStreamOrderType = (type: string) => {
  if (type === 'streamOrder') {
    return 'Stream Order';
  }
  if (type === 'streamTip') {
    return 'Stream Tip';
  }
  if (type === 'streamTipCrypto') {
    return 'Stream Crypto Tip';
  }
  return type;
};

export const getIsInterviewPending = (
  stream: GetStream_stream | MyStreams_myStreams_entities
): boolean => {
  const isInterviewPending =
    stream.isInterview &&
    stream.interview?.status === InterviewStatus.RescheduleRequested;

  return isInterviewPending;
};

export const getStreamTableStatus = (stream: MyStreams_myStreams_entities) => {
  const isStreamCanceled = stream.streamStatus === StreamStatus.Cancelled;
  const isInterviewPending = getIsInterviewPending(stream);

  if (isStreamCanceled && isInterviewPending) {
    return 'Pending';
  }
  return !stream?.deletedAt ? stream?.streamStatus : 'Deleted';
};

/** Record shape compatible with GetStream_stream_streamRecords for playlist/player */
export type StreamRecordDisplay = {
  id: number;
  streamId: string;
  videoURL: string | null;
  videoThumbnailURL: string | null;
  duration: number | null;
};

/**
 * Build a flat list of stream records from streamRecordsV2 for display.
 * Mobile: prefer portrait video; desktop: prefer landscape.
 */
export function getStreamRecordsFromV2(
  stream: GetStream_stream | null | undefined,
  isMobile: boolean
): StreamRecordDisplay[] {
  const v2 = stream?.streamRecordsV2;
  if (!v2?.length) return [];

  const streamId = stream?.id ?? '';
  const result: StreamRecordDisplay[] = [];

  for (const group of v2) {
    const landscape = group.ar?.landscape?.Mp4;
    const portrait = group.ar?.portrait?.Mp4;
    const record = isMobile ? portrait ?? landscape : landscape ?? portrait;

    if (record?.videoURL) {
      result.push({
        id: record.id,
        streamId: (record as { streamId?: string }).streamId ?? streamId,
        videoURL: record.videoURL,
        videoThumbnailURL: record.videoThumbnailURL ?? null,
        duration: (record as { duration?: number | null }).duration ?? null,
      });
    }
  }

  return result;
}

/** Returns true if stream has at least one record in streamRecordsV2 (any aspect ratio). */
export function hasStreamRecordsV2(
  stream: GetStream_stream | null | undefined
): boolean {
  const v2 = stream?.streamRecordsV2;
  if (!v2?.length) return false;
  return v2.some(
    (g) => g.ar?.landscape?.Mp4?.videoURL || g.ar?.portrait?.Mp4?.videoURL
  );
}

type GoLiveStreamSeoCopyInput = {
  storeName?: string | null;
  sport?: string | null;
};

/** Defaults become the stream page title/meta description, so keep the host and sport up front and stay under ~155 chars. */
export const buildGoLiveStreamSeoCopy = ({
  storeName,
  sport,
}: GoLiveStreamSeoCopyInput): { name: string; description: string } => {
  const host = storeName?.trim() || '';
  const sportName = sport?.trim() || '';

  if (!host) {
    return {
      name: sportName
        ? `Watch a Live ${sportName} PPV Event on MILLIONS.co`
        : 'Watch a Live PPV Event on MILLIONS.co',
      description: sportName
        ? `Watch this exclusive ${sportName.toLowerCase()} pay-per-view live stream on MILLIONS.co. Get instant access, join the live chat, and rewatch the replay anytime.`
        : 'Watch this exclusive pay-per-view live stream on MILLIONS.co. Get instant access, join the live chat, and rewatch the replay anytime.',
    };
  }

  const name = sportName
    ? `Watch ${host} Live: ${sportName} PPV on MILLIONS.co`
    : `Watch ${host} Live on MILLIONS.co`;

  const description = sportName
    ? `Watch ${host} live on MILLIONS.co — an exclusive ${sportName.toLowerCase()} pay-per-view stream. Get instant access, join the live chat, and rewatch the replay anytime.`
    : `Watch ${host} live on MILLIONS.co — an exclusive pay-per-view stream. Get instant access, join the live chat, and rewatch the replay anytime.`;

  return { name, description };
};
